import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { bannersApi } from '@/api';
import { useAuth } from '@/hooks';
import { cn } from '@/utils/cn';

export interface ActiveBannerItem {
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  imageUrl?: string;
  backgroundColor?: string;
  textColor?: string;
  ctaText?: string;
  ctaLink?: string;
  targetAudience: 'ALL' | 'PATIENTS' | 'DOCTORS';
  order: number;
  slideDurationSeconds: number;
  isActive: boolean;
  viewsCount?: number;
  clicksCount?: number;
}

export interface GlobalSalesBannerProps {
  className?: string;
  containerClassName?: string;
  placement?: 'LANDING' | 'APP';
}

export function GlobalSalesBanner({ className, containerClassName, placement = 'APP' }: GlobalSalesBannerProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const viewedBannersRef = useRef<Set<string>>(new Set());

  // 1. REGRA ABSOLUTA: Oculta automaticamente durante consultas de telemedicina
  const isConsultationRoute =
    location.pathname.includes('/telemedicine/video') ||
    location.pathname.includes('/telemedicine/waiting') ||
    location.pathname.includes('/telemedicine/session') ||
    location.pathname.includes('/medico/telemedicine/video') ||
    location.pathname.includes('/medico/telemedicine/session');

  // Consulta banners ativos da plataforma para o role do usuário logado e placement
  const { data: response } = useQuery({
    queryKey: ['banners', 'active', user?.role, placement],
    queryFn: async () => {
      const res = await bannersApi.getActiveBanners(user?.role, placement);
      return (res as any)?.data !== undefined ? (res as any).data : res;
    },
    staleTime: 1000 * 60 * 2,
    enabled: !isConsultationRoute,
  });

  const banners: ActiveBannerItem[] = response?.banners || [];
  const globalAutoplay = response?.globalAutoplay ?? true;

  const currentBanner = banners[currentIndex];

  // Registra visualização (Impression) do banner ativo no motor de vendas
  useEffect(() => {
    if (currentBanner?.id && !viewedBannersRef.current.has(currentBanner.id)) {
      viewedBannersRef.current.add(currentBanner.id);
      bannersApi.trackView(currentBanner.id).catch(() => {});
    }
  }, [currentBanner?.id]);

  // Duração do slide atual
  const durationSeconds = useMemo(() => {
    return Math.max(3, currentBanner?.slideDurationSeconds || response?.defaultSlideDurationSeconds || 6);
  }, [currentBanner, response]);

  // Barra de progresso suave animada e rotação de slides
  useEffect(() => {
    if (!globalAutoplay || isPaused || banners.length <= 1 || isConsultationRoute) {
      return;
    }

    setProgress(0);
    const intervalMs = 50;
    const stepIncrement = (intervalMs / (durationSeconds * 1000)) * 100;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentIndex((idx) => (idx + 1) % banners.length);
          return 0;
        }
        return prev + stepIncrement;
      });
    }, intervalMs);

    return () => clearInterval(progressTimer);
  }, [currentIndex, isPaused, banners.length, globalAutoplay, isConsultationRoute, durationSeconds]);

  const handleNext = useCallback(() => {
    if (banners.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % banners.length);
    setProgress(0);
  }, [banners.length]);

  const handlePrev = useCallback(() => {
    if (banners.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    setProgress(0);
  }, [banners.length]);

  const handleCtaClick = (banner: ActiveBannerItem) => {
    if (banner.id) {
      bannersApi.trackClick(banner.id).catch(() => {});
    }

    if (!banner.ctaLink) return;

    if (banner.ctaLink.startsWith('http://') || banner.ctaLink.startsWith('https://')) {
      window.open(banner.ctaLink, '_blank', 'noopener,noreferrer');
    } else {
      navigate(banner.ctaLink);
    }
  };

  if (isConsultationRoute || banners.length === 0 || !currentBanner) {
    return null;
  }

  const bgStyle = currentBanner.backgroundColor || 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0284c7 100%)';
  const textColor = currentBanner.textColor || '#FFFFFF';

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className={cn(
        "w-full px-3 sm:px-6 lg:px-8 pt-3 pb-1 z-30 select-none animate-in fade-in slide-in-from-top-3 duration-300",
        className
      )}
    >
      <div
        className={cn(
          "relative w-full max-w-[2560px] mx-auto rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl border border-white/15 dark:border-white/10 group transition-all duration-300 cursor-pointer",
          containerClassName
        )}
        onClick={() => handleCtaClick(currentBanner)}
        style={{
          background: bgStyle,
          color: textColor,
          minHeight: '130px',
        }}
      >
        {/* IMAGEM DE FUNDO WIDESCREEN TOTALMENTE NÍTIDA E CRISTALINA (Sem ofuscar o fundo) */}
        {currentBanner.imageUrl && (
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            <img
              src={currentBanner.imageUrl}
              alt={currentBanner.title}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-101"
            />
            {/* Gradiente ultra suave apenas sob o texto lateral para garantir leitura sem escurecer o anúncio */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent sm:max-w-2xl" />
          </div>
        )}

        {/* Efeito Glow quando sem imagem */}
        {!currentBanner.imageUrl && (
          <div className="absolute inset-0 z-0 pointer-events-none opacity-30 mix-blend-overlay">
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-primary/30 blur-3xl" />
          </div>
        )}

        {/* Barra de Progresso no Topo (Discreta e Elegante) */}
        {globalAutoplay && banners.length > 1 && !isPaused && (
          <div className="absolute top-0 inset-x-0 h-1 bg-white/10 z-20 overflow-hidden pointer-events-none">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 via-primary to-amber-300 transition-all duration-75 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* CORPO DO BANNER COM PROPORÇÃO WIDESCREEN CINEMATOGRÁFICA */}
        <div className="relative z-10 w-full px-5 py-4 sm:px-8 sm:py-6 lg:px-12 lg:py-7 flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6 min-h-[130px] sm:min-h-[160px] lg:min-h-[180px]">
          {/* LADO ESQUERDO: Badge, Título e Subtítulo com sombra de texto nítida */}
          <div className="flex-1 min-w-0 space-y-1.5 text-left">
            {/* Badge de Destaque */}
            <div className="flex flex-wrap items-center gap-2">
              {currentBanner.badge && (
                <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] sm:text-xs font-black uppercase tracking-widest bg-amber-400/90 text-slate-950 shadow-md backdrop-blur-md">
                  <Sparkles className="w-3.5 h-3.5 text-slate-950" />
                  <span>{currentBanner.badge}</span>
                </span>
              )}

              {banners.length > 1 && (
                <span className="text-[10px] sm:text-[11px] font-bold text-white/90 bg-black/40 px-2.5 py-0.5 rounded-full backdrop-blur-xs font-mono shadow-xs">
                  {currentIndex + 1} de {banners.length}
                </span>
              )}
            </div>

            {/* Título de Alto Impacto */}
            <h2 className="text-lg sm:text-2xl lg:text-3xl font-black tracking-tight leading-tight sm:leading-snug [text-shadow:_0_2px_10px_rgba(0,0,0,0.8)] group-hover:text-amber-200 transition-colors">
              {currentBanner.title}
            </h2>

            {/* Subtítulo / Descrição Persuasiva */}
            {currentBanner.subtitle && (
              <p className="text-xs sm:text-sm lg:text-base text-white/95 max-w-3xl leading-relaxed [text-shadow:_0_1px_6px_rgba(0,0,0,0.8)] line-clamp-2 sm:line-clamp-none font-medium">
                {currentBanner.subtitle}
              </p>
            )}
          </div>

          {/* LADO DIREITO: Botão de Conversão (CTA) + Navegação entre slides */}
          <div className="shrink-0 flex flex-wrap md:flex-col lg:flex-row items-center gap-3">
            {currentBanner.ctaText && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCtaClick(currentBanner);
                }}
                className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-3.5 bg-emerald-500 hover:bg-emerald-400 text-white font-black text-xs sm:text-sm rounded-2xl shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer border border-emerald-300/40 group-hover:scale-102"
              >
                <span>{currentBanner.ctaText}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            )}

            {/* Controles de Slides (< e >) se houver mais de 1 anúncio */}
            {banners.length > 1 && (
              <div
                className="hidden sm:flex items-center gap-1.5 bg-black/50 p-1 rounded-2xl border border-white/20 backdrop-blur-md"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={handlePrev}
                  className="p-1.5 hover:bg-white/20 rounded-xl transition-colors cursor-pointer text-white hover:text-white"
                  title="Anúncio anterior"
                  aria-label="Anúncio anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="p-1.5 hover:bg-white/20 rounded-xl transition-colors cursor-pointer text-white hover:text-white"
                  title="Próximo anúncio"
                  aria-label="Próximo anúncio"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Marcadores de Slides no Rodapé (Dots Interativos) */}
        {banners.length > 1 && (
          <div
            className="relative z-10 flex items-center justify-center gap-1.5 pb-2.5 sm:pb-3"
            onClick={(e) => e.stopPropagation()}
          >
            {banners.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setCurrentIndex(idx);
                  setProgress(0);
                }}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  currentIndex === idx
                    ? 'w-6 sm:w-8 h-1.5 bg-white shadow-md'
                    : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
                }`}
                title={`Ir para anúncio ${idx + 1}`}
                aria-label={`Ir para anúncio ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
